/** @format */

import React, {useState, useEffect} from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import Avatar from "@mui/material/Avatar";
import LoadingButton from "@mui/lab/LoadingButton";
import SendIcon from "@mui/icons-material/Send";
import SaveIcon from "@mui/icons-material/Save";
import UpdateIcon from "@mui/icons-material/Update";
import Alert from '@mui/material/Alert';
import Paper from "@mui/material/Paper";
import { useAuth } from "../Contexts/useAuth";
import Button from "@mui/material/Button";
import SelectDropDown from "./SelectDropdown";
import { toast } from 'react-toastify';
import PaymentTermsDialog from "./PaymentTermsDialog";
import { useParams, useNavigate } from "react-router-dom";
import SelectDate from "./SelectDate";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import UploadFile from "./UploadFile";
import axios from "axios";
import FileSaver from "file-saver";
import FieldArrayComponent from "./FieldArrayComponent";

dayjs.extend(customParseFormat);

const paymentTermsOptions = ["TT", "LC", "DA/DP"];

const ttPaymentMethods = [
  "TT Advance + TT Balance Before Shipment",
  "TT Advance + TT Balance After Shipment",
  "100% TT After Shipment",
  "Advance + LC",
];

export default function AddUpdateShipment({
  shipmentDetails = null,
  editMode = false,
}) {
  const { isAdmin } = useAuth();
  const [saveLoading, setSaveLoading] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [openPaymentTerms, setOpenPaymentTerms] = useState(false);
  const [selectedPaymentTerms, setSelectedPaymentTerms] = useState(() => {
    if (shipmentDetails?.shipmentDet?.paymentTerm === undefined) return null;
    if (shipmentDetails?.shipmentDet?.paymentTerm === "DA_DP") {
      return "DA/DP";
    } else return shipmentDetails?.shipmentDet?.paymentTerm;
  });

  const handlePaymentTermsDialog = () => {
    setOpenPaymentTerms(true);
  };

  const handleClose = (value) => {
    setOpenPaymentTerms(false);
    setSelectedPaymentTerms(value);
  };

  const [openPaymentMethod, setOpenPaymentMethod] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
    () => {
      if (shipmentDetails?.shipmentDet?.paymentTerm === "TT") {
        return shipmentDetails?.ttPayment?.ttCategory;
      } else return null;
    }
  );

  const handlePaymentMethodDialog = () => {
    setOpenPaymentMethod(true);
  };

  const handleMethodClose = (value) => {
    setOpenPaymentMethod(false);
    setSelectedPaymentMethod(value);
  };
  const [blDate, setBlDate] = useState(() => {
    if (shipmentDetails?.shipmentDet?.blDt === undefined || shipmentDetails?.shipmentDet?.blDt === null) return null;
    return dayjs(shipmentDetails?.shipmentDet?.blDt, "DD/MM/YYYY");
  });

  const [dispatchDate, setDispatchDate] = useState(() => {
    if (shipmentDetails?.shipmentDet?.dispatchClearanceDt === undefined || shipmentDetails?.shipmentDet?.dispatchClearanceDt === null)
      return null;
    return dayjs(
      shipmentDetails?.shipmentDet?.dispatchClearanceDt,
      "DD/MM/YYYY"
    );
  });

  const [productionDate, setProductionDate] = useState(() => {
    if (shipmentDetails?.shipmentDet?.productionGivenDt === undefined || shipmentDetails?.shipmentDet?.productionGivenDt === null)
      return null;
    return dayjs(shipmentDetails?.shipmentDet?.productionGivenDt, "DD/MM/YYYY");
  });

  const [rawDate, setRawDate] = useState(() => {
    if (shipmentDetails?.shipmentDet?.rawMaterialsReadyDt === undefined || shipmentDetails?.shipmentDet?.rawMaterialsReadyDt === null)
      return null;
    return dayjs(
      shipmentDetails?.shipmentDet?.rawMaterialsReadyDt,
      "DD/MM/YYYY"
    );
  });

  const [goodsDate, setGoodsDate] = useState(() => {
    if (shipmentDetails?.shipmentDet?.shipmentReadyDt === undefined || shipmentDetails?.shipmentDet?.shipmentReadyDt === null)
      return null;
    return dayjs(shipmentDetails?.shipmentDet?.shipmentReadyDt, "DD/MM/YYYY");
  });

  const [cooDate, setCooDate] = useState(() => {
    if (shipmentDetails?.shipmentDet?.cooDt === undefined || shipmentDetails?.shipmentDet?.cooDt === null) return null;
    return dayjs(shipmentDetails?.shipmentDet?.cooDt, "DD/MM/YYYY");
  });

  const [insuranceDate, setInsuranceDate] = useState(() => {
    if (shipmentDetails?.shipmentDet?.insuranceDt === undefined || shipmentDetails?.shipmentDet?.insuranceDt === null) return null;
    return dayjs(shipmentDetails?.shipmentDet?.insuranceDt, "DD/MM/YYYY");
  });

  const [invoiceDate, setInvoiceDate] = useState(() => {
    if (shipmentDetails?.shipmentDet?.invoiceDt === undefined || shipmentDetails?.shipmentDet?.invoiceDt === null) return null;
    return dayjs(shipmentDetails?.shipmentDet?.invoiceDt, "DD/MM/YYYY");
  });

  const [vesseEtdlDate, setVesseEtdlDate] = useState(() => {
    if (shipmentDetails?.shipmentDet?.vesselEtd === undefined || shipmentDetails?.shipmentDet?.vesselEtd === null) return null;
    return dayjs(shipmentDetails?.shipmentDet?.vesselEtd, "DD/MM/YYYY");
  });

  const [vesseEtalDate, setVesseEtalDate] = useState(() => {
    if (shipmentDetails?.shipmentDet?.vesselEta === undefined || shipmentDetails?.shipmentDet?.vesselEta === null) return null;
    return dayjs(shipmentDetails?.shipmentDet?.vesselEta, "DD/MM/YYYY");
  });

  const [lcBalancePayable, setLcBalancePayable] = useState(
    shipmentDetails?.lcPayment?.payAfterDays || 0
  );
  const [ttBeforeBalancePayable, setTtBeforeBalancePayable] = useState(
    shipmentDetails?.ttPayment?.payAfterDays || 0
  );
  const [ttAfterBalancePayable, setTtAfterBalancePayable] = useState(
    shipmentDetails?.ttPayment?.payAfterDays || 0
  );
  const [dadpBalancePayable, setDadpBalancePayable] = useState(
    shipmentDetails?.dadpPayment?.payAfterDays || 0
  );
  const [ttAdvanceAfterBalancePayable, setTtAdvanceAfterBalancePayable] =
    useState(shipmentDetails?.ttPayment?.payAfterDays || 0);
  const [ttAdvanceLcBalancePayable, setTtAdvanceLcBalancePayable] =
    useState(shipmentDetails?.ttPayment?.payAfterDays || 0);

  const [advancePercentages, setAdvancePercentages] = useState(() => {
    return shipmentDetails?.ttPayment?.advancePercentages
      ? shipmentDetails?.ttPayment?.advancePercentages.split(",").map(Number)
      : [0];
  });
  const [ttBeforBalanceDue, setTtBeforeBalanceDue] = useState(0);

  const [ttAfteradvancePercentages, setTtAfterAdvancePercentages] = useState(
    () => {
      return shipmentDetails?.ttPayment?.advancePercentages
        ? shipmentDetails?.ttPayment?.advancePercentages.split(",").map(Number)
        : [0];
    }
  );
  const [ttAfterBalanceDue, setTtAfterBalanceDue] = useState(0);

  const [advanceLcAdvancePercentages, setAdvanceLcAdvancePercentages] =
    useState(() => {
      return shipmentDetails?.ttPayment?.advancePercentages
        ? shipmentDetails?.ttPayment?.advancePercentages.split(",").map(Number)
        : [0];
    });
  const [ttAdvanceBalanceDue, setTtAdvanceBalanceDue] = useState(0);

  const [invoiceValue, setInvoiceValue] = useState(
    shipmentDetails?.shipmentDet?.invoiceVal || ""
  );

  const [reqUnit, setReqUnit] = useState(
    shipmentDetails?.shipmentDet?.reqUnit || ""
  );
  const [qtyUnit, setQtyUnit] = useState(
    shipmentDetails?.shipmentDet?.qtyUnit || ""
  );
  const [contSize, setContSize] = useState(
    shipmentDetails?.shipmentDet?.contSize || ""
  );

  const [uploadShippingMarkFile, setUploadShippingMarkFile] = useState(
    shipmentDetails?.documents || []
  );
  const [deletedShippingFiles, setDeletedShippingFiles] = useState([]);

  const [poCurrencyList, setPoCurrencyList] = useState([]);
  const [poCurrencyValue, setPoCurrencyValue] = useState(
    shipmentDetails?.shipmentDet?.currency || ""
  );

  const [invoiceCurrencyList, setInvoiceCurrencyList] = useState([]);
  const [invoiceCurrencyValue, setInvoiceCurrencyValue] = useState(
    shipmentDetails?.shipmentDet?.currency || ""
  );

  const [saveAsDraft, setSaveAsDraft] = useState(false);

  const { shipmentId } = useParams();
  console.log(shipmentId);

  const navigate = useNavigate();

  async function downloadShippingDocument(documentName) {
    if (editMode) {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/v1/shipment/${shipmentDetails?.shippingId}/download/${documentName}`,
          {
            headers: {
              Authorization: `Bearer ${JSON.parse(
                localStorage.getItem("accessToken")
              )}`,
            },
            responseType: "blob", // Ensure response is treated as a blob
          }
        );

        console.log("Response received:", response);

        const blob = new Blob([response.data], { type: response.data.type });

        console.log("Blob:", blob);

        FileSaver.saveAs(blob, documentName);
      } catch (err) {
        console.log(err);
      }
    }
  }

  function deleteFileFromShippingFiles(deletedIndex) {
    if (typeof uploadShippingMarkFile[deletedIndex] === "string") {
      const newShippingFiles = uploadShippingMarkFile.toSpliced(
        deletedIndex,
        1
      );
      console.log(newShippingFiles);
      setUploadShippingMarkFile(newShippingFiles);
      setDeletedShippingFiles((prev) => {
        return [...prev, uploadShippingMarkFile[deletedIndex]];
      });
    } else {
      console.log(deletedIndex);
      const newShippingFiles = uploadShippingMarkFile.toSpliced(
        deletedIndex,
        1
      );
      console.log(newShippingFiles);
      setUploadShippingMarkFile(newShippingFiles);
    }
  }

  async function saveShipmentDetails(shipmentData) {
    saveAsDraft ? setDraftLoading(true) : setSaveLoading(true);
    console.log("save");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v1/shipment/form-1`,
        shipmentData,
        {
          headers: {
            Authorization: `Bearer ${JSON.parse(
              localStorage.getItem("accessToken")
            )}`,
          },
        }
      );
      console.log(response);
      saveAsDraft ? setDraftLoading(false) : setSaveLoading(false);
      navigate("/shipment-and-payment-list");
      toast.success(`Shipment Details successfully ${saveAsDraft ? "Drafted" : "Submitted"}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        });
    } catch (err) {
      saveAsDraft ? setDraftLoading(false) : setSaveLoading(false);
      toast.error(`${err.message}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        });
      console.log(err);
    }
  }

  async function updateShipmentDetails(shipmentData) {
    setUpdateLoading(true);
    console.log("update");
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/v1/shipment/form-1`,
        shipmentData,
        {
          headers: {
            Authorization: `Bearer ${JSON.parse(
              localStorage.getItem("accessToken")
            )}`,
          },
        }
      );
      console.log(response);
      setUpdateLoading(false);
      navigate("/shipment-and-payment-list");
      toast.success(`Shipment Details successfully updated`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        });
    } catch (err) {
      setUpdateLoading(false);
      toast.error(`${err.message}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        });
      console.log(err);
    }
  }

  const handleShipmentSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);

    let selectedPaymentTermsDetails = {};

    let selectedTtPaymentMethod;

    switch (selectedPaymentMethod) {
      case "TT Advance + TT Balance Before Shipment":
        selectedTtPaymentMethod = {
          ttCategory: selectedPaymentMethod,
          balDueDt: data.get("tt-before-balance-due-date"),
          balDue: ttBeforBalanceDue,
          payAfterDays: ttBeforeBalancePayable,
          advancePercentages: advancePercentages.toString(),
        };
        break;

      case "TT Advance + TT Balance After Shipment":
        selectedTtPaymentMethod = {
          ttCategory: selectedPaymentMethod,
          balDueDt: data.get("tt-after-balance-due-date"),
          balDue: ttAfterBalanceDue,
          payAfterDays: ttAdvanceAfterBalancePayable,
          advancePercentages: ttAfteradvancePercentages.toString(),
        };
        break;

      case "100% TT After Shipment":
        selectedTtPaymentMethod = {
          ttCategory: selectedPaymentMethod,
          balDueDt: data.get("tt-after-shipment-balance-due-date"),
          balDue: invoiceValue,
          payAfterDays: ttAfterBalancePayable,
        };
        break;

      case "Advance + LC":
        selectedTtPaymentMethod = {
          ttCategory: selectedPaymentMethod,
          balDueDt: data.get("advance-lc-balance-due-date"),
          balDue: ttAdvanceBalanceDue,
          payAfterDays: ttAdvanceLcBalancePayable,
          advancePercentages: advanceLcAdvancePercentages.toString(),
        };
        break;

      default:
        break;
    }

    switch (selectedPaymentTerms) {
      case "TT":
        selectedPaymentTermsDetails["key"] = "ttPayment";
        selectedPaymentTermsDetails["value"] = selectedTtPaymentMethod;
        break;

      case "LC":
        selectedPaymentTermsDetails["key"] = "lcPayment";
        selectedPaymentTermsDetails["value"] = {
          payAfterDays: lcBalancePayable,
          balDue: invoiceValue,
          balDueDate: data.get("lc-balance-due-date"),
        };
        break;

      case "DA/DP":
        selectedPaymentTermsDetails["key"] = "dadpPayment";
        selectedPaymentTermsDetails["value"] = {
          balDue: invoiceValue,
          payAfterDays: dadpBalancePayable,
          invoiceDueDate: data.get("dadp-invoice-due-date"),
        };
        break;

      default:
        break;
    }

    console.log(selectedPaymentTermsDetails);

    const shipmentData = new FormData();
    const updateShipmentData = new FormData();
    const shipmentJson = {
      shipmentDet: {
        customerName: customer,
        dispatchClearanceDt: data.get("despatch-date"),
        poNumber: data.get("po-number"),
        scNumber: data.get("sc-number"),
        requirement: data.get("requirement"),
        reqUnit,
        netPoVal: data.get("net-po-value"),
        currency: invoiceCurrencyValue,
        goodsDesc: data.get("description-of-goods"),
        productionGivenDt: data.get("given-for-production"),
        rawMaterialsReadyDt: data.get("raw-material-ready"),
        shipmentReadyDt: data.get("goods-ready-for-shipment"),
        shippingAgent: data.get("shipping-agent"),
        qty: data.get("qty"),
        qtyUnit,
        noOfCont: data.get("no-of-containers"),
        contSize,
        pol: data.get("pol"),
        pod: data.get("pod"),
        blNo: data.get("bl-no"),
        blDt: data.get("bl-date"),
        cooNo: data.get("coo-no"),
        cooDt: data.get("coo-date"),
        insuranceNo: data.get("insurance-no"),
        insuranceDt: data.get("insurance-date"),
        invoiceNo: data.get("invoice-no"),
        invoiceDt: data.get("invoice-date"),
        invoiceVal: data.get("invoive-value"),
        vesselEtd: data.get("vessel-etd"),
        vesselEta: data.get("vessel-eta"),
        paymentTerm:
          selectedPaymentTerms === "DA/DP" ? "DA_DP" : selectedPaymentTerms,
        portLoading: data.get("port-of-loading"),
        portDischarge: data.get("port-of-discharge"),
        paymentDetails: data.get("details-of-payment-terms"),
        courierNo: data.get("courier-no"),
        comment: data.get("comment"),
        status: saveAsDraft ? "DRAFT" : "PENDING_PAYMENT_DET",
      },
    };

    const updateShipmentJson = {
      shipmentDet: {
        customerName: customer,
        dispatchClearanceDt: data.get("despatch-date"),
        poNumber: data.get("po-number"),
        scNumber: data.get("sc-number"),
        requirement: data.get("requirement"),
        reqUnit,
        netPoVal: data.get("net-po-value"),
        currency: invoiceCurrencyValue,
        goodsDesc: data.get("description-of-goods"),
        productionGivenDt: data.get("given-for-production"),
        rawMaterialsReadyDt: data.get("raw-material-ready"),
        shipmentReadyDt: data.get("goods-ready-for-shipment"),
        shippingAgent: data.get("shipping-agent"),
        qty: data.get("qty"),
        qtyUnit,
        noOfCont: data.get("no-of-containers"),
        contSize,
        pol: data.get("pol"),
        pod: data.get("pod"),
        blNo: data.get("bl-no"),
        blDt: data.get("bl-date"),
        cooNo: data.get("coo-no"),
        cooDt: data.get("coo-date"),
        insuranceNo: data.get("insurance-no"),
        insuranceDt: data.get("insurance-date"),
        invoiceNo: data.get("invoice-no"),
        invoiceDt: data.get("invoice-date"),
        invoiceVal: data.get("invoive-value"),
        vesselEtd: data.get("vessel-etd"),
        vesselEta: data.get("vessel-eta"),
        paymentTerm:
          selectedPaymentTerms === "DA/DP" ? "DA_DP" : selectedPaymentTerms,
        portLoading: data.get("port-of-loading"),
        portDischarge: data.get("port-of-discharge"),
        paymentDetails: data.get("details-of-payment-terms"),
        courierNo: data.get("courier-no"),
        comment: data.get("comment"),
        status: shipmentDetails?.shipmentDet?.status === "SUBMITTED" ? "SUBMITTED" : (saveAsDraft ? "DRAFT" : "PENDING_PAYMENT_DET"),
      },
      shippingId: shipmentDetails?.shippingId,
      removeDocuments: deletedShippingFiles,
    };

    shipmentJson[selectedPaymentTermsDetails["key"]] =
      selectedPaymentTermsDetails["value"];
    console.log(shipmentJson);
    shipmentData.append("json", JSON.stringify(shipmentJson));

    uploadShippingMarkFile.map((file) => {
      shipmentData.append("files", file[0]);
    });

    updateShipmentJson[selectedPaymentTermsDetails["key"]] =
      selectedPaymentTermsDetails["value"];
    console.log(updateShipmentJson);
    updateShipmentData.append("json", JSON.stringify(updateShipmentJson));

    uploadShippingMarkFile.map((file) => {
      if (!(typeof file === "string"))
        updateShipmentData.append("files", file[0]);
    });

    (editMode || shipmentDetails != null)
      ? updateShipmentDetails(updateShipmentData)
      : saveShipmentDetails(shipmentData);
  };

  const [customersList, setCustomersList] = useState([]);
  const [customer, setCustomer] = useState(
    shipmentDetails?.shipmentDet?.customerName || ""
  );

  async function getCustomers() {
    try {
      const customerResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/v1/customers`,
        {
          headers: {
            Authorization: `Bearer ${JSON.parse(
              localStorage.getItem("accessToken")
            )}`,
          },
        }
      );
      setCustomersList(
        customerResponse.data.map((customer) => {
          return {
            name: customer.name,
            value: customer.name,
          };
        })
      );
    } catch (err) {
      console.log(err);
    }
  }

  async function getCurrencyList() {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/v1/masterdata/getAllCurrencies`
      );
      const currency_list = response.data.map((currency) => {
        return {
          name: currency.currencyName,
          value: currency.currency,
        };
      });
      setInvoiceCurrencyList(currency_list);
      setPoCurrencyList(currency_list);
    } catch (err) {
      console.log(err);
    }
  }

  useEffect(() => {
    console.log(selectedPaymentTerms);
  }, [selectedPaymentTerms]);

  useEffect(() => {
    getCustomers();
    getCurrencyList();
  }, []);

  useEffect(() => {
    console.log(ttAdvanceBalanceDue);
    console.log(ttBeforBalanceDue);
    console.log(ttAfterBalanceDue);
  }, [ttAdvanceBalanceDue, ttBeforBalanceDue, ttAfterBalanceDue]);

  useEffect(() => {
    console.log(uploadShippingMarkFile);
  }, [uploadShippingMarkFile]);

  useEffect(() => {
    console.log(deletedShippingFiles);
  }, [deletedShippingFiles]);

  useEffect(() => {
    console.log(shipmentDetails);
  }, [shipmentDetails]);

  return (
    <Box
      sx={{
        marginTop: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "70%",
      }}>
      <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
        <LocalShippingIcon />
      </Avatar>
      <Typography component="h1" variant="h5">
        {!editMode ? "Add New Shipment Details" : "Update Shipment Details"}
      </Typography>
      {(shipmentDetails?.updateLock && !isAdmin) && <Alert severity="error" sx={{my: 1}}>{`Shipment ${shipmentDetails?.shippingId} is locked for editing! Please contact with Admin.`}</Alert>}
      <Box
        component="form"
        onSubmit={handleShipmentSubmit}
        sx={{
          mt: 1,
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}>
        <SelectDate
          dateLabel="Despatch Clearance Date"
          dateName="despatch-date"
          value={dispatchDate}
          onChange={setDispatchDate}
        />

        <TextField
          margin="normal"
          required
          id="po-number"
          label="PO Number"
          name="po-number"
          defaultValue={shipmentDetails?.shipmentDet?.poNumber}
        />

        <TextField
          margin="normal"
          required
          id="sc-number"
          label="SC Number"
          name="sc-number"
          defaultValue={shipmentDetails?.shipmentDet?.scNumber}
        />

        <SelectDropDown
          options={customersList}
          dropdownLabel={"Select one of the Customer"}
          value={customer}
          setValue={setCustomer}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 2,
          }}>
          <TextField
            fullWidth
            margin="normal"
            id="requirement"
            label="Requirement"
            name="requirement"
            type="number"
            defaultValue={shipmentDetails?.shipmentDet?.requirement}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <SelectDropDown
            options={[
              { name: "Pieces", value: "Pieces" },
              { name: "Set", value: "Set" },
              { name: "Tonnage", value: "Tonnage" },
            ]}
            dropdownLabel="Unit"
            value={reqUnit}
            setValue={setReqUnit}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 2,
          }}>
          <TextField
            fullWidth
            margin="normal"
            id="net-po-value"
            label="Net PO Value"
            name="net-po-value"
            type="number"
            defaultValue={shipmentDetails?.shipmentDet?.netPoVal}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <SelectDropDown
            options={poCurrencyList}
            dropdownLabel="Currency"
            value={poCurrencyValue}
            setValue={setPoCurrencyValue}
          />
        </Box>
        <TextField
          margin="normal"
          id="description-of-goods"
          label="Description Of Goods"
          name="description-of-goods"
          defaultValue={shipmentDetails?.shipmentDet?.goodsDesc}
        />

        {uploadShippingMarkFile?.length !== 0 && (
          <>
            <Typography color="secondary" sx={{ pt: 2 }}>
              Selected Shipping Document(s) :
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              alignItems="center"
              sx={{ my: 1, width: "100%", height: "100px" }}>
              {uploadShippingMarkFile?.map((fileDetails, index) => {
                console.log(fileDetails[0].name || fileDetails);
                return (
                  <Chip
                    key={index}
                    label={fileDetails[0].name || fileDetails}
                    onClick={() =>
                      downloadShippingDocument(fileDetails[0].name || fileDetails)
                    }
                    onDelete={() => deleteFileFromShippingFiles(index)}
                    color="primary"
                  />
                );
              })}
            </Stack>
          </>
        )}

        <UploadFile
          name={
            uploadShippingMarkFile?.length === 0
              ? "Upload Shipping Document"
              : "Add Another Shipping Document"
          }
          setFile={setUploadShippingMarkFile}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-evenly",
            alignItems: "center",
            flexWrap: 'wrap',
            my: 2,
          }}>
          <SelectDate
            dateLabel="Given For Production"
            dateName="given-for-production"
            value={productionDate}
            onChange={setProductionDate}
          />
          <SelectDate
            dateLabel="Raw Material Ready"
            dateName="raw-material-ready"
            value={rawDate}
            onChange={setRawDate}
          />
          <SelectDate
            dateLabel="Goods Ready For Shipment"
            dateName="goods-ready-for-shipment"
            value={goodsDate}
            onChange={setGoodsDate}
          />
        </Box>
        <TextField
          margin="normal"
          required
          id="shipping-agent"
          label="FRT Forwarder/Shipping Agent"
          name="shipping-agent"
          defaultValue={shipmentDetails?.shipmentDet?.shippingAgent}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 2,
          }}>
          <TextField
            fullWidth
            margin="normal"
            id="qty"
            label="QTY"
            name="qty"
            type="number"
            defaultValue={shipmentDetails?.shipmentDet?.qty}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <SelectDropDown
            options={[
              { name: "Pieces", value: "Pieces" },
              { name: "Set", value: "Set" },
              { name: "Tonnage", value: "Tonnage" },
            ]}
            dropdownLabel={"Unit"}
            value={qtyUnit}
            setValue={setQtyUnit}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 2,
          }}>
          <TextField
            fullWidth
            margin="normal"
            id="no-of-containers"
            label="No Of Containers"
            name="no-of-containers"
            type="number"
            defaultValue={shipmentDetails?.shipmentDet?.noOfCont}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <SelectDropDown
            options={[
              { name: "20 FT", value: "20 FT" },
              { name: "40 FT", value: "40 FT" },
              { name: "40 FT HQ", value: "40 FT HQ" },
            ]}
            dropdownLabel="Container Size"
            value={contSize}
            setValue={setContSize}
          />
        </Box>
        <TextField
          margin="normal"
          id="pol"
          label="POL"
          name="pol"
          defaultValue={shipmentDetails?.shipmentDet?.pol}
        />
        <TextField
          margin="normal"
          id="pod"
          label="POD"
          name="pod"
          defaultValue={shipmentDetails?.shipmentDet?.pod}
        />

        <TextField
          margin="normal"
          required
          id="bl-no"
          label="B/L No"
          name="bl-no"
          defaultValue={shipmentDetails?.shipmentDet?.blNo}
        />
        <SelectDate
          dateLabel="B/L Date"
          dateName="bl-date"
          value={blDate}
          onChange={setBlDate}
        />

        <TextField
          margin="normal"
          id="coo-no"
          label="COO No"
          name="coo-no"
          defaultValue={shipmentDetails?.shipmentDet?.cooNo}
        />
        <SelectDate
          dateLabel="COO Date"
          dateName="coo-date"
          value={cooDate}
          onChange={setCooDate}
        />
        <TextField
          margin="normal"
          id="insurance-no"
          label="Insurance No"
          name="insurance-no"
          defaultValue={shipmentDetails?.shipmentDet?.insuranceNo}
        />
        <SelectDate
          dateLabel="Insurance Date"
          dateName="insurance-date"
          value={insuranceDate}
          onChange={setInsuranceDate}
        />
        <TextField
          margin="normal"
          required
          id="invoice-no"
          label="Invoice No"
          name="invoice-no"
          defaultValue={shipmentDetails?.shipmentDet?.invoiceNo}
        />
        <SelectDate
          dateLabel="Invoice Date"
          dateName="invoice-date"
          value={invoiceDate}
          onChange={setInvoiceDate}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 2,
            my: 1,
          }}>
          <TextField
            fullWidth
            required
            margin="normal"
            id="invoive-value"
            label="Invoice Value"
            name="invoive-value"
            type="number"
            value={invoiceValue}
            defaultValue={shipmentDetails?.shipmentDet?.invoiceVal}
            onChange={(e) => setInvoiceValue(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <SelectDropDown
            options={invoiceCurrencyList}
            dropdownLabel="Invoice Currency"
            value={invoiceCurrencyValue}
            setValue={setInvoiceCurrencyValue}
          />
        </Box>
        <SelectDate
          dateLabel="Vessel ETD"
          dateName="vessel-etd"
          value={vesseEtdlDate}
          onChange={setVesseEtdlDate}
        />
        <TextField
          fullWidth
          margin="normal"
          required
          id="port-of-loading"
          label="Port Of Loading"
          name="port-of-loading"
          defaultValue={shipmentDetails?.shipmentDet?.portLoading}
        />
        <SelectDate
          dateLabel="Vessel ETA"
          dateName="vessel-eta"
          value={vesseEtalDate}
          onChange={setVesseEtalDate}
        />
        <TextField
          fullWidth
          margin="normal"
          required
          id="port-of-discharge"
          label="Port Of Discharge"
          name="port-of-discharge"
          defaultValue={shipmentDetails?.shipmentDet?.portDischarge}
        />
        <Button
          variant="outlined"
          onClick={handlePaymentTermsDialog}
          sx={{ my: 2 }}>
          Payment Terms
        </Button>

        <PaymentTermsDialog
          dialogTitle="Payment Terms"
          open={openPaymentTerms}
          onClose={handleClose}
          selectedValue={setSelectedPaymentTerms}
          list={paymentTermsOptions}
        />

        <TextField
          id="filled-multiline-static"
          label="Details of Payment Terms"
          name="details-of-payment-terms"
          multiline
          rows={4}
          variant="filled"
          defaultValue={shipmentDetails?.shipmentDet?.paymentDetails}
          sx={{ mb: 2 }}
        />

        {(() => {
          switch (selectedPaymentTerms) {
            case "TT":
              return (
                <>
                  <Paper elevation={3} sx={{ p: 2 }}>
                    <Typography color="secondary">
                      Selected Payment Terms: TT
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={handlePaymentMethodDialog}
                      sx={{ my: 2 }}>
                      Payment Method
                    </Button>
                    <PaymentTermsDialog
                      dialogTitle="Payment Methods"
                      open={openPaymentMethod}
                      onClose={handleMethodClose}
                      selectedValue={setSelectedPaymentMethod}
                      list={ttPaymentMethods}
                    />

                    {(() => {
                      switch (selectedPaymentMethod) {
                        case "TT Advance + TT Balance Before Shipment":
                          return (
                            <>
                              <Typography color="secondary">
                                Selected Payment Method : TT Advance + TT
                                Balance Before Shipment
                              </Typography>

                              <FieldArrayComponent
                                initialValues={advancePercentages}
                                setValues={setAdvancePercentages}
                                setBalanceDue={setTtBeforeBalanceDue}
                                invoiceValue={invoiceValue}
                                invoiceCurrency={invoiceCurrencyValue}
                              />
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-start",
                                  alignItems: "center",
                                  gap: 1,
                                }}>
                                <Typography>Balance Payable after</Typography>
                                <TextField
                                  size="small"
                                  id="tt-advance-lc-balance-payable"
                                  name="tt-advance-lc-balance-payable"
                                  type="number"
                                  variant="filled"
                                  margin="dense"
                                  value={ttBeforeBalancePayable}
                                  onChange={(e) =>
                                    setTtBeforeBalancePayable(e.target.value)
                                  }
                                  helperText="numbers only"
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                />
                                <Typography>days of Shipment</Typography>
                                <Chip
                                  color="primary"
                                  variant="outlined"
                                  label={
                                    ttBeforeBalancePayable > 0 ? "Usance" :  "At Sight"
                                  }
                                />
                              </Box>
                              <SelectDate
                                readOnly={true}
                                dateLabel="Balance Due Date"
                                dateName="tt-before-balance-due-date"
                                value={blDate?.add(ttBeforeBalancePayable, "d")}
                              />
                            </>
                          );
                        case "TT Advance + TT Balance After Shipment":
                          return (
                            <>
                              <Typography color="secondary">
                                Selected Payment Method : TT Advance + TT
                                Balance After Shipment
                              </Typography>
                              <FieldArrayComponent
                                initialValues={ttAfteradvancePercentages}
                                setValues={setTtAfterAdvancePercentages}
                                setBalanceDue={setTtAfterBalanceDue}
                                invoiceValue={invoiceValue}
                                invoiceCurrency={invoiceCurrencyValue}
                              />
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-start",
                                  alignItems: "center",
                                  gap: 1,
                                }}>
                                <Typography>Balance Payable after</Typography>
                                <TextField
                                  size="small"
                                  id="tt-advance-after-balance-payable"
                                  name="tt-advance-after-balance-payable"
                                  type="number"
                                  variant="filled"
                                  margin="dense"
                                  value={ttAdvanceAfterBalancePayable}
                                  onChange={(e) =>
                                    setTtAdvanceAfterBalancePayable(
                                      e.target.value
                                    )
                                  }
                                  helperText="numbers only"
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                />
                                <Typography>days of Shipment</Typography>
                              </Box>
                              <SelectDate
                                readOnly={true}
                                dateLabel="Balance Due Date"
                                dateName="tt-after-balance-due-date"
                                value={blDate?.add(
                                  ttAdvanceAfterBalancePayable,
                                  "d"
                                )}
                              />
                            </>
                          );
                        case "100% TT After Shipment":
                          return (
                            <>
                              <Typography color="secondary">
                                Selected Payment Method : 100% TT After Shipment
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-start",
                                  alignItems: "center",
                                  gap: 1,
                                }}>
                                <Typography sx={{ my: 2 }}>
                                  TT Balance Due :
                                </Typography>
                                <Typography color="primary">
                                  {invoiceValue}
                                </Typography>
                                {invoiceCurrencyValue && (
                                  <Chip
                                    color="primary"
                                    variant="outlined"
                                    label={invoiceCurrencyValue}
                                  />
                                )}
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-start",
                                  alignItems: "center",
                                  gap: 1,
                                }}>
                                <Typography>Balance Payable after</Typography>
                                <TextField
                                  size="small"
                                  id="tt-balance-payable"
                                  name="tt-balance-payable"
                                  type="number"
                                  variant="filled"
                                  margin="dense"
                                  value={ttAfterBalancePayable}
                                  onChange={(e) =>
                                    setTtAfterBalancePayable(e.target.value)
                                  }
                                  helperText="numbers only"
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                />
                                <Typography>days of Shipment</Typography>
                              </Box>
                              <SelectDate
                                readOnly={true}
                                dateLabel="Balance Due Date"
                                dateName="tt-after-shipment-balance-due-date"
                                value={blDate?.add(ttAfterBalancePayable, "d")}
                              />
                            </>
                          );
                        case "Advance + LC":
                          return (
                            <>
                              <Typography color="secondary">
                                Selected Payment Method : Advance + LC
                              </Typography>
                              <FieldArrayComponent
                                initialValues={advanceLcAdvancePercentages}
                                setValues={setAdvanceLcAdvancePercentages}
                                setBalanceDue={setTtAdvanceBalanceDue}
                                invoiceValue={invoiceValue}
                                invoiceCurrency={invoiceCurrencyValue}
                              />
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-start",
                                  alignItems: "center",
                                  gap: 1,
                                }}>
                                <Typography>Balance Payable after</Typography>
                                <TextField
                                  size="small"
                                  id="tt-advance-lc-balance-payable"
                                  name="tt-advance-lc-balance-payable"
                                  type="number"
                                  variant="filled"
                                  margin="dense"
                                  value={ttAdvanceLcBalancePayable}
                                  onChange={(e) =>
                                    setTtAdvanceLcBalancePayable(e.target.value)
                                  }
                                  helperText="numbers only"
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                />
                                <Typography>days of Shipment</Typography>
                                <Chip
                                  color="primary"
                                  variant="outlined"
                                  label={
                                    ttAdvanceLcBalancePayable > 0 ? "Usance" :  "At Sight"
                                  }
                                />
                              </Box>
                              <SelectDate
                                readOnly={true}
                                dateLabel="Balance Due Date"
                                dateName="advance-lc-balance-due-date"
                                value={blDate?.add(
                                  ttAdvanceLcBalancePayable,
                                  "d"
                                )}
                              />
                            </>
                          );
                        default:
                          return null;
                      }
                    })()}
                  </Paper>
                </>
              );
            case "LC":
              return (
                <>
                  <Paper elevation={3} sx={{ p: 2 }}>
                    <Typography color="secondary">
                      Selected Payment Terms: LC
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        gap: 1,
                      }}>
                      <Typography>Balance Payable after</Typography>
                      <TextField
                        size="small"
                        id="lc-balance-payable"
                        name="lc-balance-payable"
                        type="number"
                        variant="filled"
                        margin="dense"
                        value={lcBalancePayable}
                        onChange={(e) => setLcBalancePayable(e.target.value)}
                        helperText="numbers only"
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                      <Typography>days of Shipment</Typography>
                      <Chip
                        color="primary"
                        variant="outlined"
                        label={lcBalancePayable > 0 ? "Usance" :  "At Sight"}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        gap: 1,
                      }}>
                      <Typography sx={{ my: 2 }}>LC Balance Due :</Typography>
                      <Typography color="primary">{invoiceValue}</Typography>
                      {invoiceCurrencyValue && (
                        <Chip
                          color="primary"
                          variant="outlined"
                          label={invoiceCurrencyValue}
                        />
                      )}
                    </Box>
                    <SelectDate
                      readOnly={true}
                      dateLabel="Balance Due Date"
                      dateName="lc-balance-due-date"
                      value={blDate?.add(lcBalancePayable, "d")}
                    />
                  </Paper>
                </>
              );
            case "DA/DP":
              return (
                <>
                  <Paper elevation={3} sx={{ p: 2 }}>
                    <Typography color="secondary">
                      Selected Payment Terms: DA/DP
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        gap: 1,
                        my: 2,
                      }}>
                      <Typography>DA/DP Balance Due : </Typography>
                      <Typography color="primary">{invoiceValue}</Typography>
                      {invoiceCurrencyValue && (
                        <Chip
                          color="primary"
                          variant="outlined"
                          label={invoiceCurrencyValue}
                        />
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        gap: 1,
                      }}>
                      <Typography>Balance Payable after</Typography>
                      <TextField
                        size="small"
                        id="da/dp-balance-payable"
                        name="balance-payable"
                        type="number"
                        variant="filled"
                        margin="dense"
                        value={dadpBalancePayable}
                        onChange={(e) => setDadpBalancePayable(e.target.value)}
                        helperText="numbers only"
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                      <Typography>days of Shipment</Typography>
                      <Chip
                        color="primary"
                        variant="outlined"
                        label={dadpBalancePayable > 0 ? "Usance" :  "At Sight"}
                      />
                    </Box>
                    <SelectDate
                      readOnly={true}
                      dateLabel="Invoive Due Date"
                      dateName="dadp-invoice-due-date"
                      value={blDate?.add(dadpBalancePayable, "d")}
                    />
                  </Paper>
                </>
              );
            default:
              return null;
          }
        })()}

        <TextField
          margin="normal"
          required
          id="courier-no"
          label="Courier No"
          name="courier-no"
          defaultValue={shipmentDetails?.shipmentDet?.courierNo}
        />

        <TextField
          id="filled-multiline-static"
          label="Comment"
          name="comment"
          multiline
          rows={4}
          variant="filled"
          defaultValue={shipmentDetails?.shipmentDet?.comment}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
          }}>
          <Button
            variant="contained"
            sx={{ mt: 3, mb: 2, bgcolor: "secondary.main" }}
            onClick={() => navigate("/shipment-and-payment-list")}>
            Back
          </Button>
          {editMode ? (
            <LoadingButton
              type="submit"
              variant="contained"
              sx={{ mt: 3, mb: 2, bgcolor: "secondary.main" }}
              loading={updateLoading}
              endIcon={<UpdateIcon />}
              disabled={shipmentDetails?.updateLock && !isAdmin}
              loadingPosition="end">
              Update
            </LoadingButton>
          ) : (
            <>
              <LoadingButton
                type="submit"
                onClick={(e) => setSaveAsDraft(true)}
                variant="contained"
                sx={{ mt: 3, mb: 2, bgcolor: "secondary.main" }}
                loading={draftLoading}
                startIcon={<SaveIcon />}
                loadingPosition="start">
                Save As Draft
              </LoadingButton>
              <LoadingButton
                type="submit"
                onClick={(e) => setSaveAsDraft(false)}
                variant="contained"
                sx={{ mt: 3, mb: 2, bgcolor: "secondary.main" }}
                loading={saveLoading}
                endIcon={<SendIcon />}
                loadingPosition="end">
                Submit
              </LoadingButton>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
